<template>
  <nuxt-link
    v-bind="{ ...props, to, target, external, prefetch, ariaCurrentValue, role }"
    :class="{ [activeHashClass]: isMounted && route.hash && path?.includes(route.hash) && path?.includes(route.path) }"
    data-test-id="base-link"
    :trailing-slash="!external ? 'remove' : undefined"
    @click="hooks.callHook('link:clicked')"
  >
    <slot />
  </nuxt-link>
</template>

<script lang="ts" setup>
import type { HookResult } from '@nuxt/schema'
import type { RouteLocationRaw } from 'vue-router'

const {
  // type-casting is used due to router-link TS bug that doesn't allow null value to be passed
  ariaCurrentValue = null as unknown as undefined,
  /**
   * it needs to be defaulted to `false` explicitly
   * because the default value is `undefined` in the router-link component
   * https://github.com/nuxt/nuxt/blob/107d0954c605fba6526d0cf3eaf3c273fe3cd1f4/packages/nuxt/src/app/components/nuxt-link.ts#L282
   * https://digital.vfc.com/jira/browse/GLOBAL15-72774
   */
  prefetch = false,
  activeHashClass = '',
  ...props
} = defineProps<{
  to?: RouteLocationRaw
  target?: '_blank' | '_self' | null
  prefetch?: boolean
  ariaCurrentValue?: 'location' | 'false' | 'true' | 'page' | 'time' | 'date' | 'step' | undefined
  noRel?: boolean
  activeHashClass?: string
}>()

declare module '#app' {
  interface RuntimeNuxtHooks {
    'link:clicked': () => HookResult
  }
}

const route = useRoute()
const { hooks } = useNuxtApp()

const to = computed(() => {
  // Works around a weird behaviour in router-link which breaks anchor links for us.
  // When `to` prop values that are strings and are not full URLs (e.g. anchors, as in our case)
  // are parsed and normalised, they're appended only to the path of the current location,
  // which causes the current query string, if any, to be dropped
  if (typeof props.to === 'string' && props.to.startsWith('#'))
    return `${route.fullPath.split('#')[0]}${props.to}`

  return useLocalisedRoute(props.to!)
})
const path = computed<string>(() => typeof to.value === 'object' ? to.value.path : to.value)
const external = computed(() => !!(typeof props.to === 'string' && props.to.match(/^https?:\/\//)))
const target = computed(() => props.target || (external.value ? '_blank' : undefined))
const role = computed(() => to.value?.search?.(/#modal=|#panel=/) !== -1 ? 'button' : 'link')
const isMounted = useMounted()
</script>
